import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["indicator", "spinner", "arrow", "text", "content"]
  static values = { 
    threshold: { type: Number, default: 80 },
    maxPull: { type: Number, default: 150 },
    refreshing: { type: Boolean, default: false }
  }

  connect() {
    console.log('🔄 Pull to Refresh Controller connected')
    
    // Track touch events
    this.startY = 0
    this.currentY = 0
    this.pulling = false
    
    // Bind event handlers
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    
    // Add event listeners
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.element.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    this.element.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    
    // Also support mouse events for testing on desktop
    this.element.addEventListener('mousedown', this.handleMouseDown.bind(this))
    
    // Initialize indicator position
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.style.transform = 'translateY(-100%)'
      this.indicatorTarget.style.transition = 'none'
    }
  }

  disconnect() {
    this.element.removeEventListener('touchstart', this.handleTouchStart)
    this.element.removeEventListener('touchmove', this.handleTouchMove)
    this.element.removeEventListener('touchend', this.handleTouchEnd)
  }

  handleTouchStart(e) {
    // Only start pull if we're at the top of the page
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
    
    if (scrollTop === 0 && !this.refreshingValue) {
      this.startY = e.touches[0].clientY
      this.pulling = true
      
      // Remove transition for smooth pulling
      if (this.hasIndicatorTarget) {
        this.indicatorTarget.style.transition = 'none'
      }
    }
  }

  handleTouchMove(e) {
    if (!this.pulling || this.refreshingValue) return
    
    this.currentY = e.touches[0].clientY
    const pullDistance = Math.min(this.currentY - this.startY, this.maxPullValue)
    
    if (pullDistance > 0) {
      // Prevent default to stop page bounce on iOS
      e.preventDefault()
      
      // Apply resistance for more natural feel
      const resistance = Math.min(pullDistance / this.maxPullValue, 1)
      const actualDistance = this.maxPullValue * (1 - Math.pow(1 - resistance, 2))
      
      // Update indicator position
      if (this.hasIndicatorTarget) {
        this.indicatorTarget.style.transform = `translateY(${actualDistance - 60}px)`
        this.indicatorTarget.style.opacity = Math.min(pullDistance / this.thresholdValue, 1)
      }
      
      // Update content position for visual feedback
      if (this.hasContentTarget) {
        this.contentTarget.style.transform = `translateY(${actualDistance}px)`
      }
      
      // Update indicator state
      if (pullDistance >= this.thresholdValue) {
        this.setReadyToRefresh()
      } else {
        this.setPulling()
      }
      
      // Rotate arrow based on pull distance
      if (this.hasArrowTarget) {
        const rotation = Math.min((pullDistance / this.thresholdValue) * 180, 180)
        this.arrowTarget.style.transform = `rotate(${rotation}deg)`
      }
    }
  }

  handleTouchEnd(e) {
    if (!this.pulling) return
    
    const pullDistance = this.currentY - this.startY
    
    if (pullDistance >= this.thresholdValue && !this.refreshingValue) {
      // Trigger refresh
      this.refresh()
    } else {
      // Reset position
      this.reset()
    }
    
    this.pulling = false
  }

  // Mouse events for desktop testing
  handleMouseDown(e) {
    // Only in development/testing
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      
      if (scrollTop === 0 && !this.refreshingValue && e.shiftKey) {
        this.startY = e.clientY
        this.pulling = true
        
        if (this.hasIndicatorTarget) {
          this.indicatorTarget.style.transition = 'none'
        }
        
        const handleMouseMove = (e) => {
          if (!this.pulling) return
          
          this.currentY = e.clientY
          const pullDistance = Math.min(this.currentY - this.startY, this.maxPullValue)
          
          if (pullDistance > 0) {
            e.preventDefault()
            
            const resistance = Math.min(pullDistance / this.maxPullValue, 1)
            const actualDistance = this.maxPullValue * (1 - Math.pow(1 - resistance, 2))
            
            if (this.hasIndicatorTarget) {
              this.indicatorTarget.style.transform = `translateY(${actualDistance - 60}px)`
              this.indicatorTarget.style.opacity = Math.min(pullDistance / this.thresholdValue, 1)
            }
            
            if (this.hasContentTarget) {
              this.contentTarget.style.transform = `translateY(${actualDistance}px)`
            }
            
            if (pullDistance >= this.thresholdValue) {
              this.setReadyToRefresh()
            } else {
              this.setPulling()
            }
            
            if (this.hasArrowTarget) {
              const rotation = Math.min((pullDistance / this.thresholdValue) * 180, 180)
              this.arrowTarget.style.transform = `rotate(${rotation}deg)`
            }
          }
        }
        
        const handleMouseUp = (e) => {
          const pullDistance = this.currentY - this.startY
          
          if (pullDistance >= this.thresholdValue && !this.refreshingValue) {
            this.refresh()
          } else {
            this.reset()
          }
          
          this.pulling = false
          document.removeEventListener('mousemove', handleMouseMove)
          document.removeEventListener('mouseup', handleMouseUp)
        }
        
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
      }
    }
  }

  setPulling() {
    if (this.hasTextTarget) {
      this.textTarget.textContent = 'Pull to refresh'
    }
    
    if (this.hasArrowTarget && this.hasSpinnerTarget) {
      this.arrowTarget.classList.remove('hidden')
      this.spinnerTarget.classList.add('hidden')
    }
  }

  setReadyToRefresh() {
    if (this.hasTextTarget) {
      this.textTarget.textContent = 'Release to refresh'
    }
    
    // Haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(10)
    }
  }

  setRefreshing() {
    this.refreshingValue = true
    
    if (this.hasTextTarget) {
      this.textTarget.textContent = 'Refreshing...'
    }
    
    if (this.hasArrowTarget && this.hasSpinnerTarget) {
      this.arrowTarget.classList.add('hidden')
      this.spinnerTarget.classList.remove('hidden')
    }
    
    // Keep indicator visible during refresh
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.style.transition = 'transform 0.3s ease-out'
      this.indicatorTarget.style.transform = 'translateY(0)'
      this.indicatorTarget.style.opacity = '1'
    }
    
    // Reset content position
    if (this.hasContentTarget) {
      this.contentTarget.style.transition = 'transform 0.3s ease-out'
      this.contentTarget.style.transform = 'translateY(60px)'
    }
  }

  async refresh() {
    console.log('🔄 Pull to refresh: Loading new posts...')
    this.setRefreshing()
    
    try {
      // Find the new-posts controller and use its loadNewPosts method
      const newPostsElement = document.querySelector('[data-controller~="new-posts"]')
      if (newPostsElement) {
        const app = window.Stimulus
        if (app) {
          const newPostsController = app.getControllerForElementAndIdentifier(newPostsElement, 'new-posts')
          if (newPostsController && newPostsController.loadNewPosts) {
            // Use the same logic as the "Load New Posts" button
            await newPostsController.loadNewPosts()
            console.log('✅ Pull to refresh: New posts loaded successfully!')
            this.showSuccessFeedback()
          } else {
            console.log('❌ Could not find new-posts controller or loadNewPosts method')
            await this.fallbackRefresh()
          }
        } else {
          console.log('❌ Stimulus not available')
          await this.fallbackRefresh()
        }
      } else {
        console.log('❌ Could not find new-posts controller element')
        await this.fallbackRefresh()
      }
    } catch (error) {
      console.error('❌ Error during pull to refresh:', error)
      this.showErrorFeedback()
    } finally {
      // Reset after a delay
      setTimeout(() => {
        this.reset()
      }, 500)
    }
  }
  
  async fallbackRefresh() {
    // Fallback to page reload if new-posts controller is not available
    console.log('🔄 Falling back to page reload...')
    Turbo.visit(window.location.href, { action: 'replace' })
  }
  
  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  reset() {
    this.refreshingValue = false
    
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.style.transition = 'all 0.3s ease-out'
      this.indicatorTarget.style.transform = 'translateY(-100%)'
      this.indicatorTarget.style.opacity = '0'
    }
    
    if (this.hasContentTarget) {
      this.contentTarget.style.transition = 'transform 0.3s ease-out'
      this.contentTarget.style.transform = 'translateY(0)'
    }
    
    if (this.hasArrowTarget) {
      this.arrowTarget.style.transform = 'rotate(0deg)'
    }
    
    // Reset text
    if (this.hasTextTarget) {
      this.textTarget.textContent = 'Pull to refresh'
    }
    
    // Show arrow, hide spinner
    if (this.hasArrowTarget && this.hasSpinnerTarget) {
      this.arrowTarget.classList.remove('hidden')
      this.spinnerTarget.classList.add('hidden')
    }
  }

  showSuccessFeedback() {
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate([50, 30, 50])
    }
    
    // Visual feedback
    if (this.hasTextTarget) {
      this.textTarget.textContent = '✓ Updated!'
    }
  }

  showErrorFeedback() {
    if (this.hasTextTarget) {
      this.textTarget.textContent = '✗ Failed to refresh'
    }
  }
}